/**
 * Parent Links Database References
 *
 * Parent-child relationship links
 * Path: /clients/{clientKey}/parent_links/{linkId}
 */

import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase';

/* ═══════════════════════════════════════════════════════════
   PARENT LINKS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the parent_links collection for a client
 * Path: /clients/{clientKey}/parent_links
 */
export const ParentLinksRef_Collection = (clientKey: string = 'athleto'): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'parent_links');
};

/**
 * Reference to a specific parent link document
 * Path: /clients/{clientKey}/parent_links/{linkId}
 */
export const ParentLinkRef_Document = (
  linkId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'parent_links', linkId);
};

/* ═══════════════════════════════════════════════════════════
   PARENT LINK QUERIES
   ═══════════════════════════════════════════════════════════ */

/**
 * Query for parent links by parent ID
 * Gets all children linked to a parent
 */
export const ParentLinksByParentRef_Query = (
  parentId: string,
  status?: 'pending' | 'active' | 'revoked',
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  if (status) {
    return query(
      ParentLinksRef_Collection(clientKey),
      where('parent_id', '==', parentId),
      where('status', '==', status),
      orderBy('created_at', 'desc')
    );
  }
  return query(
    ParentLinksRef_Collection(clientKey),
    where('parent_id', '==', parentId),
    orderBy('created_at', 'desc')
  );
};

/**
 * Query for parent links by child ID
 * Gets the parent linked to a child (should be max 1 active)
 */
export const ParentLinksByChildRef_Query = (
  childId: string,
  status?: 'pending' | 'active' | 'revoked',
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  if (status) {
    return query(
      ParentLinksRef_Collection(clientKey),
      where('child_id', '==', childId),
      where('status', '==', status),
      orderBy('created_at', 'desc')
    );
  }
  return query(
    ParentLinksRef_Collection(clientKey),
    where('child_id', '==', childId),
    orderBy('created_at', 'desc')
  );
};

/**
 * Query for parent link by invite code
 * Used when parent is linking with a code
 */
export const ParentLinkByInviteCodeRef_Query = (
  inviteCode: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    ParentLinksRef_Collection(clientKey),
    where('invite_code', '==', inviteCode),
    where('status', '==', 'pending'),
    limit(1)
  );
};

/**
 * Query for active parent links by team
 * Gets all parents with children on a specific team
 */
export const ParentLinksByTeamRef_Query = (
  teamId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    ParentLinksRef_Collection(clientKey),
    where('child_team_ids', 'array-contains', teamId),
    where('status', '==', 'active')
  );
};
